import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import moment from "moment";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import {
    adminRmnCrawlsEndpoint,
    adminRmnCrawlEndpoint,
    adminRmnCrawlCancelEndpoint,
    adminRmnCrawlResumeEndpoint,
    adminRmnCrawlGraphEndpoint,
    adminRmnClearCrawlDataEndpoint,
} from "../../../api/endpoints";
import {
    RmnCrawl,
    RmnCrawlGraphLink,
    RmnCrawlGraphNode,
    RmnCrawlStatus,
} from "../../../types/rmn";
import { ForceNetworkGraph } from "../../../components/NetworkGraph/ForceNetworkGraph";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { DotLottleLoader } from "../../../components/Loader";
import useSocket from "../../../hooks/useSocket";
import eventEmitter from "../../../utils/eventEmitter";

const STATUS_CHIP: Record<RmnCrawlStatus, { label: string; bg: string; fg: string }> = {
    queued: { label: "Queued", bg: "var(--surface-2)", fg: "var(--text-2)" },
    running: { label: "Running", bg: "rgba(36,116,221,.14)", fg: "#2474DD" },
    completed: { label: "Completed", bg: "var(--up-bg)", fg: "var(--up)" },
    failed: { label: "Failed", bg: "var(--down-bg)", fg: "var(--down)" },
    canceled: { label: "Canceled", bg: "var(--surface-2)", fg: "var(--muted)" },
};

// Node styling for the live crawl network: gold root (the RMN), blue
// categories shrinking with depth, small grey product leaves, green brands.
const CRAWL_NODE_COLORS: Record<string, string> = {
    root: "#AB854A",
    category: "#2474DD",
    product: "var(--muted)",
    brand: "#5A9E6F",
};

const crawlNodeColor = (n: RmnCrawlGraphNode): string =>
    CRAWL_NODE_COLORS[n.type] || "#94A3B8";

const crawlNodeRadius = (n: RmnCrawlGraphNode): number => {
    if (n.type === "root") return 14;
    if (n.type === "product") return 3;
    if (n.type === "brand") return 6;
    return Math.max(5, 8 - (n.depth || 0));
};

// Products are far too numerous to label; the dots alone show the growth.
const crawlNodeLabel = (n: RmnCrawlGraphNode) =>
    n.type === "product" ? null : { text: n.name || "", bold: n.type === "root" };

const statusChip = (status: RmnCrawlStatus) => {
    const c = STATUS_CHIP[status] || STATUS_CHIP.queued;
    return (
        <Chip
            label={c.label}
            size="small"
            sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600 }}
        />
    );
};

const formatTime = (value?: string | null) =>
    value ? moment(value).format("MMM DD, YYYY LT") : "—";

interface CrawlPageCounts {
    queued?: number;
    fetched?: number;
    failed?: number;
}

interface StatProps {
    label: string;
    value: number | string;
}

function Stat({ label, value }: StatProps) {
    return (
        <Box className="flex flex-col items-center px-4 py-2 rounded-lg bg-surface-2 border border-border-token min-w-[110px]">
            <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    );
}

interface CrawlPanelProps {
    brandId: number;
}

// Internal-admin crawl management for an RMN brand: latest-crawl status card,
// live crawl network (socket deltas + snapshot reconciliation), and history.
export function CrawlPanel({ brandId }: CrawlPanelProps) {
    const { socket } = useSocket();
    const [crawls, setCrawls] = useState<RmnCrawl[]>([]);
    const [loading, setLoading] = useState(true);
    const [pageCounts, setPageCounts] = useState<CrawlPageCounts | null>(null);
    const [graph, setGraph] = useState<{
        nodes: RmnCrawlGraphNode[];
        links: RmnCrawlGraphLink[];
    }>({ nodes: [], links: [] });
    const [liveStats, setLiveStats] = useState<RmnCrawl["stats"] | null>(null);
    const [startOpen, setStartOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [clearOpen, setClearOpen] = useState(false);
    const [maxDepth, setMaxDepth] = useState("4");
    const [maxPages, setMaxPages] = useState("2000");
    const [rootUrl, setRootUrl] = useState("");
    const [scoped, setScoped] = useState(false);
    const [busy, setBusy] = useState(false);

    const latest: RmnCrawl | null = crawls.length ? crawls[0] : null;
    const crawlId = latest ? latest.id : null;
    const isActive =
        !!latest && (latest.status === "queued" || latest.status === "running");

    const fetchCrawls = useCallback(async () => {
        try {
            const res = await axiosInstant.get(adminRmnCrawlsEndpoint, {
                params: { brand_id: brandId },
            });
            setCrawls(res.data && Array.isArray(res.data.rows) ? res.data.rows : []);
        } catch (err) {
            toast.error("Failed to load crawls");
        } finally {
            setLoading(false);
        }
    }, [brandId]);

    useEffect(() => {
        setLoading(true);
        fetchCrawls();
    }, [fetchCrawls]);

    // While a crawl is active, poll its detail every ~8s so status flips
    // (completed/failed) update the UI even if sockets misbehave.
    useEffect(() => {
        if (!crawlId || !isActive) return undefined;
        let stopped = false;
        const tick = async () => {
            try {
                const res = await axiosInstant.get(adminRmnCrawlEndpoint(crawlId));
                if (stopped || !res.data) return;
                const row = res.data;
                if (row.page_counts) setPageCounts(row.page_counts);
                setCrawls((prev) =>
                    prev.map((c) => (c.id === row.id ? { ...c, ...row } : c))
                );
            } catch (err) {
                // transient — next tick retries
            }
        };
        const interval = window.setInterval(tick, 8000);
        tick();
        return () => {
            stopped = true;
            window.clearInterval(interval);
        };
    }, [crawlId, isActive]);

    // Snapshot fetch replaces the graph wholesale — the ForceNetworkGraph
    // position cache preserves the layout, so reconciliation is invisible
    // unless deltas were actually missed.
    const fetchGraph = useCallback(async (id: number) => {
        try {
            const res = await axiosInstant.get(adminRmnCrawlGraphEndpoint(id));
            const data = res.data || {};
            setGraph({
                nodes: Array.isArray(data.nodes) ? data.nodes : [],
                links: Array.isArray(data.links) ? data.links : [],
            });
            if (data.stats) setLiveStats(data.stats);
        } catch (err) {
            // graph is best-effort; counters still come from status polling
        }
    }, []);

    useEffect(() => {
        setGraph({ nodes: [], links: [] });
        setLiveStats(null);
        setPageCounts(null);
        if (crawlId) fetchGraph(crawlId);
    }, [crawlId, fetchGraph]);

    // Live growth while running: join the crawl's socket room and append
    // node/link deltas (deduped); reconcile with a snapshot poll every ~20s.
    useEffect(() => {
        if (!crawlId || !isActive) return undefined;
        const room = `crawl:${crawlId}`;

        const handleDelta = (payload: unknown) => {
            let data: any = payload;
            if (typeof data === "string") {
                try {
                    data = JSON.parse(data);
                } catch (err) {
                    return;
                }
            }
            if (!data || Number(data.crawlId) !== crawlId) return;
            if (data.stats) setLiveStats(data.stats);
            const incomingNodes: RmnCrawlGraphNode[] = Array.isArray(data.nodes)
                ? data.nodes
                : [];
            const incomingLinks: RmnCrawlGraphLink[] = Array.isArray(data.links)
                ? data.links
                : [];
            if (!incomingNodes.length && !incomingLinks.length) return;
            setGraph((prev) => {
                const ids = new Set(prev.nodes.map((n) => n.id));
                const nodes = [...prev.nodes];
                incomingNodes.forEach((n) => {
                    if (n && n.id && !ids.has(n.id)) {
                        ids.add(n.id);
                        nodes.push(n);
                    }
                });
                const linkKey = (l: RmnCrawlGraphLink) => `${l.source}->${l.target}`;
                const keys = new Set(prev.links.map(linkKey));
                const links = [...prev.links];
                incomingLinks.forEach((l) => {
                    if (!l || keys.has(linkKey(l))) return;
                    // ForceGraph2D throws on links to unknown node ids; the 20s
                    // snapshot reconciliation picks up anything dropped here.
                    if (!ids.has(String(l.source)) || !ids.has(String(l.target))) return;
                    keys.add(linkKey(l));
                    links.push({ source: l.source, target: l.target });
                });
                if (
                    nodes.length === prev.nodes.length &&
                    links.length === prev.links.length
                ) {
                    return prev;
                }
                return { nodes, links };
            });
        };

        eventEmitter.on("crawlProgress", handleDelta);
        if (socket) (socket as any).emit("join", room);
        const interval = window.setInterval(() => fetchGraph(crawlId), 20000);
        return () => {
            eventEmitter.off("crawlProgress", handleDelta);
            if (socket) (socket as any).emit("leave", room);
            window.clearInterval(interval);
        };
    }, [crawlId, isActive, socket, fetchGraph]);

    const startCrawl = async () => {
        setBusy(true);
        try {
            const payload: Record<string, unknown> = { brand_id: brandId };
            const depth = parseInt(maxDepth, 10);
            if (!Number.isNaN(depth) && depth > 0) payload.max_depth = depth;
            const pages = parseInt(maxPages, 10);
            if (!Number.isNaN(pages) && pages > 0) payload.max_pages = pages;
            if (rootUrl.trim()) {
                payload.root_url = rootUrl.trim();
                if (scoped) payload.scoped = true;
            }
            await axiosInstant.post(adminRmnCrawlsEndpoint, payload);
            toast.success("Crawl started");
            setStartOpen(false);
            await fetchCrawls();
        } catch (err: any) {
            // failedError serializes ApiError bodies as either a bare string
            // or { error } — read the code, not just the HTTP status.
            const data = err && err.response ? err.response.data : null;
            const code = typeof data === "string" ? data : data && data.error;
            if (code === "CrawlAlreadyActive") {
                toast.error("A crawl is already active for this brand.");
            } else if (code === "NoAllowedDomains") {
                toast.error(
                    "No allowed domains configured — add them in the White Label tab first."
                );
            } else if (code === "DomainNotAllowed") {
                toast.error(
                    "The start URL is outside the brand's allowed domains."
                );
            } else if (code === "InvalidUrl") {
                toast.error("The start URL is not a valid URL.");
            } else if (code === "ScopedCrawlRequiresUrl") {
                toast.error("Category-scoped crawls need a start URL.");
            } else {
                toast.error("Failed to start crawl");
            }
        } finally {
            setBusy(false);
        }
    };

    const cancelCrawl = async () => {
        if (!crawlId) return;
        setBusy(true);
        try {
            await axiosInstant.post(adminRmnCrawlCancelEndpoint(crawlId));
            toast.success("Crawl canceled");
            setCancelOpen(false);
            await fetchCrawls();
        } catch (err) {
            toast.error("Failed to cancel crawl");
        } finally {
            setBusy(false);
        }
    };

    const resumeCrawl = async () => {
        if (!crawlId) return;
        setBusy(true);
        try {
            await axiosInstant.post(adminRmnCrawlResumeEndpoint(crawlId));
            toast.success("Crawl resumed");
            await fetchCrawls();
        } catch (err) {
            toast.error("Failed to resume crawl");
        } finally {
            setBusy(false);
        }
    };

    const clearCrawlData = async () => {
        setBusy(true);
        try {
            await axiosInstant.delete(adminRmnClearCrawlDataEndpoint(brandId));
            toast.success("Crawl data cleared");
            setClearOpen(false);
            setGraph({ nodes: [], links: [] });
            setLiveStats(null);
            setPageCounts(null);
            await fetchCrawls();
        } catch (err: any) {
            const data = err && err.response ? err.response.data : null;
            const code = typeof data === "string" ? data : data && data.error;
            if (code === "CrawlAlreadyActive") {
                toast.error("A crawl is running — cancel it before clearing.");
            } else {
                toast.error("Failed to clear crawl data");
            }
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <Box className="flex justify-center py-16">
                <DotLottleLoader fill={false} />
            </Box>
        );
    }

    const stats = liveStats || (latest ? latest.stats : null) || {};
    const pagesFetched =
        pageCounts && typeof pageCounts.fetched === "number"
            ? pageCounts.fetched
            : stats.pages_fetched || 0;

    return (
        <Box className="flex flex-col gap-6">
            {/* Latest crawl status card */}
            <Paper variant="outlined" className="p-4">
                {!latest ? (
                    <Box className="flex flex-col items-center gap-3 py-8">
                        <Typography variant="body1" sx={{ opacity: 0.7 }}>
                            No crawls yet for this brand. Start one to build its
                            category tree and product catalog.
                        </Typography>
                        <Button variant="contained" onClick={() => setStartOpen(true)}>
                            Start crawl
                        </Button>
                    </Box>
                ) : (
                    <Box className="flex flex-col gap-4">
                        <Box className="flex flex-wrap items-center justify-between gap-3">
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Typography variant="h6">Latest crawl</Typography>
                                {statusChip(latest.status)}
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                {isActive && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        disabled={busy}
                                        onClick={() => setCancelOpen(true)}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                {(latest.status === "failed" ||
                                    latest.status === "canceled") && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={busy}
                                        onClick={resumeCrawl}
                                    >
                                        Resume
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={busy || isActive}
                                    onClick={() => setStartOpen(true)}
                                >
                                    Start new crawl
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    disabled={busy || isActive}
                                    onClick={() => setClearOpen(true)}
                                >
                                    Clear crawl data
                                </Button>
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" color="text.secondary">
                                Root:{" "}
                                <Box component="span" sx={{ fontFamily: "monospace" }}>
                                    {latest.root_url}
                                </Box>
                            </Typography>
                            {latest.scoped && (
                                <Chip
                                    size="small"
                                    label={
                                        latest.scope_category
                                            ? `Scoped to ${latest.scope_category.name}`
                                            : "Category-scoped"
                                    }
                                    sx={{
                                        bgcolor: "#EFE9FB",
                                        color: "#6D4FC4",
                                        fontWeight: 600,
                                    }}
                                />
                            )}
                        </Stack>

                        <Box className="flex flex-wrap gap-3">
                            <Stat label="Pages fetched" value={pagesFetched} />
                            {!!latest.scoped && (
                                <Stat
                                    label="Out of scope"
                                    value={stats.pages_skipped || 0}
                                />
                            )}
                            <Stat
                                label="Categories"
                                value={stats.categories_upserted || 0}
                            />
                            <Stat
                                label="Products"
                                value={stats.products_discovered || 0}
                            />
                            <Stat label="Brands" value={stats.brands_discovered || 0} />
                            <Stat label="Errors" value={stats.errors || 0} />
                        </Box>

                        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                            <Typography variant="caption" color="text.secondary">
                                Started: {formatTime(latest.started_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Finished: {formatTime(latest.finished_at)}
                            </Typography>
                        </Stack>

                        {latest.status === "failed" && latest.error && (
                            <Typography variant="body2" sx={{ color: "var(--down)" }}>
                                {latest.error}
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>

            {/* Live crawl network */}
            {latest && (
                <Box className="flex flex-col gap-2">
                    <Box className="flex flex-wrap items-center justify-between gap-2">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Crawl network
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {(["root", "category", "product", "brand"] as const).map(
                                (t) => (
                                    <Stack
                                        key={t}
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                    >
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                bgcolor: CRAWL_NODE_COLORS[t],
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ textTransform: "capitalize" }}
                                        >
                                            {t === "root" ? "RMN" : t}
                                        </Typography>
                                    </Stack>
                                )
                            )}
                        </Stack>
                    </Box>
                    <ForceNetworkGraph<RmnCrawlGraphNode>
                        nodes={graph.nodes}
                        links={graph.links}
                        nodeColor={crawlNodeColor}
                        nodeRadius={crawlNodeRadius}
                        nodeLabel={crawlNodeLabel}
                        rootId="root"
                        height={520}
                    />
                </Box>
            )}

            {/* History */}
            {crawls.length > 0 && (
                <Box className="flex flex-col gap-2">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Crawl history
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Pages fetched</TableCell>
                                    <TableCell align="right">Categories</TableCell>
                                    <TableCell>Started</TableCell>
                                    <TableCell>Finished</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {crawls.map((c) => (
                                    <TableRow key={c.id} hover>
                                        <TableCell>{c.id}</TableCell>
                                        <TableCell>{statusChip(c.status)}</TableCell>
                                        <TableCell align="right">
                                            {(c.stats && c.stats.pages_fetched) || 0}
                                        </TableCell>
                                        <TableCell align="right">
                                            {(c.stats && c.stats.categories_upserted) || 0}
                                        </TableCell>
                                        <TableCell>{formatTime(c.started_at)}</TableCell>
                                        <TableCell>{formatTime(c.finished_at)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Start-crawl dialog: ConfirmationDialog can't host inputs
                (description is string-only), so this is a small purpose-built
                dialog matching its layout. */}
            <Dialog
                open={startOpen}
                fullWidth
                maxWidth="sm"
                onClose={() => !busy && setStartOpen(false)}
            >
                <DialogTitle>Start a new crawl?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Crawling fetches pages through ScrapingBee and consumes
                            API credits — one credit per page, up to the page cap.
                            Only whitelisted domains are crawled.
                        </Typography>
                        <TextField
                            label="Start URL (optional)"
                            size="small"
                            fullWidth
                            value={rootUrl}
                            onChange={(e) => setRootUrl(e.target.value)}
                            placeholder="https://www.samsclub.com/"
                            helperText="Defaults to the first allowed domain's homepage. Must be within the allowed domains."
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={scoped && !!rootUrl.trim()}
                                    disabled={!rootUrl.trim()}
                                    onChange={(e) => setScoped(e.target.checked)}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">
                                        Crawl only this category
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Paste a category URL above and the crawl
                                        stays inside that category — its
                                        subcategories and products. Pages outside
                                        it are skipped.
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: "flex-start", mx: 0 }}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Max depth"
                                size="small"
                                type="number"
                                value={maxDepth}
                                onChange={(e) => setMaxDepth(e.target.value)}
                                inputProps={{ min: 1, max: 10 }}
                            />
                            <TextField
                                label="Max pages"
                                size="small"
                                type="number"
                                value={maxPages}
                                onChange={(e) => setMaxPages(e.target.value)}
                                inputProps={{ min: 1, max: 20000 }}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions className="!px-6 !py-4">
                    <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() => setStartOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={busy} onClick={startCrawl}>
                        Start crawl
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                onConfirm={cancelCrawl}
                title="Cancel this crawl?"
                description="The crawl stops after the current page. You can resume it later from where it left off."
                confirmLabel="Cancel crawl"
                cancelLabel="Keep running"
                disabled={busy}
            />

            <ConfirmationDialog
                open={clearOpen}
                onClose={() => setClearOpen(false)}
                onConfirm={clearCrawlData}
                title="Clear all crawl data?"
                description="Deletes this RMN's entire crawl history — crawled pages, the category tree (including manually added categories), and all discovered products and brands. Imported brands and products stay in the canonical catalog, and past audit reports keep working. This cannot be undone."
                confirmLabel="Clear crawl data"
                cancelLabel="Keep data"
                disabled={busy}
            />
        </Box>
    );
}
